/**
 * First-login sequence for a creator.
 *
 * Email, then profile, then one connected channel, then staff approval.
 * The steps sit in the page and cannot be skipped. Campaigns, earnings
 * and payouts stay locked until status is ACTIVE.
 */
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Eyebrow, PageHeader, StateBlock } from "@ui/primitives";
import { fontSize } from "@design/index";
import { api } from "@app/shared/api";
import type { NavItem } from "@app/shared/PortalShell";
import { ProfileForm } from "./Profile";
import { SocialAccounts } from "./Social";

export type CreatorAccessStep =
  | "email" | "profile" | "channels" | "pending" | "ready" | "blocked";

export interface CreatorAccess {
  step: CreatorAccessStep;
  profileComplete: boolean;
  channels: number;
  status: string;
}

export function useCreatorAccess(enabled: boolean) {
  const [access, setAccess] = useState<CreatorAccess | "error" | null>(null);

  const reload = useCallback(async (): Promise<CreatorAccess | null> => {
    if (!enabled) return null;
    const r = await api.get<CreatorAccess>("/onboarding/access");
    if (!r.ok || !r.data.step) {
      setAccess("error");
      return null;
    }
    setAccess(r.data);
    return r.data;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setAccess(null);
      return;
    }
    let live = true;
    void api.get<CreatorAccess>("/onboarding/access").then((r) => {
      if (!live) return;
      setAccess(r.ok && r.data.step ? r.data : "error");
    });
    return () => { live = false; };
  }, [enabled]);

  return { access, reload };
}

/** Product tabs lock until the account is approved. Profile stays open. */
export function creatorNav(nav: readonly NavItem[], step: CreatorAccessStep | null): readonly NavItem[] {
  if (step === "ready") return nav;
  const socialOpen = step === "channels" || step === "pending";
  return nav.map((item) => {
    if (item.href === "/onboarding") return item;
    if (socialOpen && item.href === "/social") return item;
    return { ...item, locked: true };
  });
}

export function keepSearch(path: string): string {
  const search = window.location.search;
  return search ? `${path}${search}` : path;
}

const STEPS = [
  { id: "profile", label: "Profile" },
  { id: "channels", label: "Channel" },
  { id: "pending", label: "Verification" },
] as const;

function stepIndex(step: CreatorAccessStep): number {
  if (step === "channels") return 1;
  if (step === "pending" || step === "ready" || step === "blocked") return 2;
  return 0;
}

export function SetupCard({ step }: { step: CreatorAccessStep }) {
  const index = stepIndex(step);
  const title = index === 0
    ? "Complete your profile"
    : index === 1
      ? "Connect at least one channel"
      : "Awaiting verification";
  const detail = step === "email"
    ? "Confirm the link we sent. The profile cannot be saved until that address is verified."
    : index === 0
      ? "Display name, date of birth and country come next. You need to be 18 or older. Campaigns open after this, a connected channel, and staff approval."
      : index === 1
        ? "Connect YouTube, TikTok, Instagram, Facebook or Reddit. One account is enough. Your profile is sent for verification after that."
        : "Your profile has been sent for verification and is awaiting verification.";
  return (
    <Card padding={22} style={{ display: "grid", gap: 14 }}>
      <Eyebrow>Get started</Eyebrow>
      <h1 style={{
        margin: 0, fontSize: fontSize.h2, fontWeight: 750, letterSpacing: "-0.02em",
      }}>
        {title}
      </h1>
      <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6, maxWidth: 640 }}>
        {detail}
      </p>
      <ol style={{
        display: "grid", gap: 8, margin: 0, padding: 0, listStyle: "none",
      }}>
        {STEPS.map((item, i) => {
          const current = i === index;
          const done = i < index;
          return (
            <li key={item.id} aria-current={current ? "step" : undefined} style={{
              display: "flex", alignItems: "center", gap: 10,
              opacity: done || current ? 1 : 0.55,
            }}>
              <span aria-hidden="true" style={{
                width: 24, height: 24, borderRadius: 99,
                display: "grid", placeItems: "center",
                fontSize: fontSize.label, fontWeight: 700,
                background: done ? "var(--tone-ok-bg)" : current ? "var(--accent)" : "var(--surface-2)",
                color: current ? "var(--accent-fg)" : done ? "var(--tone-ok-fg)" : "var(--text)",
              }}>
                {done ? "✓" : i + 1}
              </span>
              <span style={{ fontWeight: current ? 700 : 550 }}>{item.label}</span>
              {current ? <Badge tone="process">Now</Badge> : null}
              {done ? <Badge tone="ok">Done</Badge> : null}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

export function ProfileStep({
  step, email, onSaved, onSignOut,
}: {
  step: CreatorAccessStep;
  email?: string | undefined;
  onSaved: () => void;
  onSignOut: () => void;
}) {
  return (
    <>
      <SetupCard step={step} />
      {step === "email" ? (
        <StateBlock
          kind="forbidden"
          title="Verify your email first"
          detail={email
            ? `We sent a link to ${email}. Confirm it, then come back to finish your profile.`
            : "Confirm your email, then come back to finish your profile."}
        />
      ) : (
        <ProfileForm onSaved={onSaved} />
      )}
      {step === "channels" ? (
        <p style={{ margin: 0, fontSize: fontSize.body }}>
          <a href={keepSearch("/social")}>Continue to connect a channel</a>
        </p>
      ) : null}
      <div>
        <Button size="sm" variant="ghost" onClick={onSignOut}>Sign out</Button>
      </div>
    </>
  );
}

export function ChannelStep({
  onConnections, onSignOut,
}: {
  onConnections: (active: number) => void;
  onSignOut: () => void;
}) {
  return (
    <>
      <SetupCard step="channels" />
      <SocialAccounts embedded onConnections={onConnections} />
      <div>
        <Button size="sm" variant="ghost" onClick={onSignOut}>Sign out</Button>
      </div>
    </>
  );
}

export function AwaitingBanner() {
  return (
    <Card padding={20} style={{ display: "grid", gap: 8 }}>
      <Eyebrow>Verification</Eyebrow>
      <strong style={{ fontSize: fontSize.h3, letterSpacing: "-0.02em" }}>
        Your profile has been sent for verification and is awaiting verification.
      </strong>
      <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
        Campaigns, performance, earnings and payouts stay locked until a member of staff approves your account.
        You can still update this profile and your social accounts.
      </p>
    </Card>
  );
}

export function AwaitingVerification() {
  return (
    <>
      <PageHeader
        eyebrow="Verification"
        title="Awaiting verification"
        description="Your profile has been sent for verification and is awaiting verification. Until a member of staff approves it, campaigns and the other work tabs stay locked."
      />
      <Card padding={28} style={{
        display: "grid", gap: 10, justifyItems: "center", textAlign: "center",
      }}>
        <strong style={{ fontSize: fontSize.h3 }}>Campaigns are locked</strong>
        <p style={{ margin: 0, maxWidth: 460, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          You can still update your profile and social accounts. Joining a campaign opens after approval.
        </p>
        <a href={keepSearch("/onboarding")} style={{ color: "var(--accent)", fontWeight: 650 }}>
          Review your profile
        </a>
      </Card>
    </>
  );
}

export function setupPath(path: string, step: CreatorAccessStep): boolean {
  if (path === "/onboarding") {
    return step === "email" || step === "profile" || step === "channels" || step === "pending";
  }
  if (path === "/social") return step === "channels" || step === "pending";
  return false;
}
